import { formatIDR } from "@marketplace/shared/utils";
import { Button, Switch, Text, Tooltip, UnstyledButton } from "@marketplace/ui";
import { type DataTableColumn } from "@marketplace/ui/data-table";
import { getRouteApi } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineInfo, MdOutlineTextSnippet } from "react-icons/md";

import { userDataAtom } from "@/modules/auth/atoms/auth-atom";

import { ProductListTableActions } from "../components/product-list-table-actions";
import { ProductStatusPill } from "../components/product-status-pill";
import { PromoIcon } from "../components/promo-pill";
import type { ProductListItem } from "../types";

const route = getRouteApi("/products/list/");

export const useProductListTableColumns = (isDraft: boolean = false) => {
  const { page } = route.useSearch();
  const { t } = useTranslation("product");
  const userData = useAtomValue(userDataAtom);
  const navigate = useNavigate();
  const userRoles = userData?.roles || [];
  const isCanReview = userRoles.some((role) =>
    ["seller-admin", "superadmin"].includes(role)
  );
  const isSuperAdmin = userRoles.some((role) => role === "superadmin");
  const productListTableColumns = useMemo<DataTableColumn<ProductListItem>[]>(
    () => [
      {
        accessor: "no",
        title: "No",
        render: (_, index) => (page - 1) * 10 + index + 1
      },
      {
        accessor: "product_id",
        title: t("table.productList.columns.productId"),
        render: ({ id }) => id ?? "-"
      },
      {
        accessor: "name",
        title: t("table.productList.columns.productName"),
        render: ({
          name,
          missing_platform_fee_type_detected,
          is_publish,
          is_promo_available
        }) => {
          return (
            <>
              <div className="flex items-center gap-1">
                <span>{name}</span>
                {is_promo_available && <PromoIcon />}

                {missing_platform_fee_type_detected && is_publish && (
                  <Tooltip
                    label={
                      <div className="flex items-center gap-1">
                        <Text type="heading" size="ss" weight="semibold">
                          {t("table.productList.marginNotSet")}
                        </Text>
                      </div>
                    }
                    position="top"
                    withArrow
                    multiline
                    classNames={{
                      tooltip:
                        "!rounded-lg !bg-[#454545] p-[14px] w-[276px] !text-white !text-h-ss !font-semibold"
                    }}
                  >
                    <UnstyledButton className="flex items-center justify-center  ">
                      <div className="w-4 h-4 flex items-center justify-center rounded-full bg-[#FF9500]">
                        <MdOutlineInfo className="w-3 h-3 text-white" />
                      </div>
                    </UnstyledButton>
                  </Tooltip>
                )}
              </div>
            </>
          );
        }
      },
      {
        accessor: "package",
        title: t("table.productList.columns.package"),
        render: ({ product_package }) => product_package?.[0]?.name ?? "-"
      },
      {
        accessor: "price",
        title: t("table.productList.columns.price"),
        render: ({ product_package }) => {
          let displayPrice = 0;

          if (product_package?.length) {
            const firstPackage = product_package[0];

            if (firstPackage.variants?.length) {
              const firstVariant = firstPackage.variants[0];
              displayPrice =
                firstVariant.price_with_comission > 0
                  ? firstVariant.price_with_comission
                  : firstVariant.price;
            }
          }

          return formatIDR(displayPrice);
        }
      },
      ...(isSuperAdmin
        ? [
            {
              accessor: "is_lintasarta_product",
              title: t("table.productList.columns.type"),
              render: ({ is_lintasarta_product }: ProductListItem) => {
                return (
                  <>
                    {is_lintasarta_product ? (
                      <div className="bg-[#F2F5FF] flex justify-center items-center w-[120px] h-6 rounded ">
                        <Text color="#0648FF">
                          {t("table.productList.productType.internal")}
                        </Text>
                      </div>
                    ) : (
                      <div className="bg-[#F1FDFF] flex justify-center items-center w-[120px] h-6 rounded ">
                        <Text color="#30B0C7">
                          {t("table.productList.productType.external")}
                        </Text>
                      </div>
                    )}
                  </>
                );
              }
            }
          ]
        : []),
      {
        accessor: "product_status",
        title: t("table.productList.columns.status"),
        render: ({ product_status, is_publish, slug }) => {
          if (is_publish) {
            return <ProductStatusPill status="PUBLISHED" />;
          }
          if (is_publish === false && product_status === "active") {
            return <ProductStatusPill status="UNPUBLISHED" />;
          }
          if (product_status === "in_review") {
            return (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  navigate({ to: `/products/list/${slug}/review` })
                }
                disabled={!isCanReview}
                className="w-[120px]"
              >
                <div className="flex items-center gap-1">
                  <MdOutlineTextSnippet size={16} />
                  {t("table.productList.columns.review")}
                </div>
              </Button>
            );
          }
          return <ProductStatusPill status="DRAFT" />;
        }
      },

      ...(!isDraft
        ? [
            {
              accessor: "is_automation" as const,
              title: t("table.productList.columns.automation"),
              render: ({ is_automation }: ProductListItem) => (
                <Switch
                  label={<Text>{is_automation ? "On" : "Off"}</Text>}
                  labelPosition="left"
                  checked={is_automation}
                  readOnly
                  // onChange={() => {
                  //   if (is_automation) {
                  //     // Deactivate automation
                  //     deactivateMutation.mutate({
                  //       product_id: id
                  //     });
                  //   } else {
                  //     // Open modal - it will check for existing data
                  //     setAutomationModal({
                  //       opened: true,
                  //       productId: id,
                  //       isAutomationActive: false
                  //     });
                  //   }
                  // }}
                  classNames={{
                    labelWrapper: "flex items-center justify-center"
                  }}
                />
              )
            }
          ]
        : []),
      {
        accessor: "actions",
        title: "",
        render: (record) => (
          <div className="flex justify-end">
            <ProductListTableActions productData={record} />
          </div>
        )
      }
    ],
    [t, page, isDraft, isCanReview, isSuperAdmin, navigate]
  );

  return productListTableColumns;
};
